import { Component, OnInit } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { DatetimeService } from '../../../services/datetime/datetime.service';
import { PlantsService } from '../../company/data/plants.service';
import { OrdersService } from '../../projects/data/orders.service';
import { ProjectsService } from '../../projects/data/projects.service';
import {
  DeviationCategory,
  DeviationSeverity,
  DeviationSubjectType,
  OrderDeviationType,
  ProjectDeviationType,
  ReceiptDeviationType
} from '../../../shared/deviations/types';
import {
  DashboardDate,
  DashboardDayPeriod
} from '../period-pickers/dashboard-day-filter/dashboard-day-filter.component';
import {
  DashboardDeviation,
  OrderDeliveries,
  OrdersStatus,
  OrderStatus,
  PlantCapacities,
  PlantData,
  ProjectDeviations,
  ProjectsOrdersDashboardData,
  Summary
} from './types';
import { createProjectsOrdersMockData } from './operational.mock';

const useMockData = true;

@Component({
  selector: 'dashboard-operational',
  templateUrl: './operational.component.html',
  styleUrls: ['./operational.component.scss']
})
export class DashboardOperationalComponent implements OnInit {
  plants: any[] = [];
  plantId: number = -1;
  date: DashboardDate;
  deviations: ProjectDeviations[] = [];
  plantsData: PlantData[] = [];
  summary: Summary = this.createEmptySummary();
  ordersStatus: OrdersStatus = this.createEmptyOrdersStatus();
  deviationsSummary: DashboardDeviation[] = [];
  orderDeliveries: OrderDeliveries = this.createEmptyOrderDeliveries();
  plantCapacities: PlantCapacities = this.createEmptyPlantCapacities();

  constructor(
    private PlantsService: PlantsService,
    private ProjectsService: ProjectsService,
    private OrdersService: OrdersService,
    private DatetimeService: DatetimeService
  ) { }

  ngOnInit(): void {
    this.setToday();

    if(useMockData) {
      this.getData();
      return;
    }

    this.PlantsService.getData(null).subscribe(
      (response: any) => {
        this.plants = this.unwrapData<any[]>(response) ?? [];
        this.getData();
      },
      (error: any) => console.error(error)
    );
  }

  getData(): void {
    if(useMockData) {
      this.applyData(createProjectsOrdersMockData(this.date, this.plantId));
      return;
    }

    this.getApiData();
  }

  setSummary(): void {
    if(this.plantId > 0) {
      const plantData = this.plantsData.find(item => item.id === this.plantId);
      this.summary = plantData ? { ...plantData.summary } : this.createEmptySummary();
      return;
    }

    const summary = this.createEmptySummary();
    for(const plantData of this.plantsData) {
      summary.plannedAmount += plantData.summary.plannedAmount;
      summary.deliveredAmount += plantData.summary.deliveredAmount;
      summary.remainingAmount += plantData.summary.remainingAmount;
      summary.projectCount += plantData.summary.projectCount;
      summary.orderCount += plantData.summary.orderCount;
      summary.deviationCount += plantData.summary.deviationCount;
    }
    summary.progressPercentage = summary.plannedAmount > 0
      ? Math.round(summary.deliveredAmount / summary.plannedAmount * 100)
      : 0;
    this.summary = summary;
  }

  setdeviationsSummary(): void {
    const groupedDeviations = new Map<string, DashboardDeviation>();

    for(const deviation of this.deviations) {
      const key = `${deviation.subjectType}:${deviation.type}`;
      const summaryItem = groupedDeviations.get(key);
      if(summaryItem)
        summaryItem.count = (summaryItem.count ?? 0) + deviation.count;
      else
        groupedDeviations.set(key, this.createDeviationSummary(deviation));
    }

    this.deviationsSummary = Array.from(groupedDeviations.values());
    this.summary.deviationCount = this.deviationsSummary.reduce(
      (total, deviation) => total + (deviation.count ?? 0),
      0
    );
  }

  plantChange(plantId: number): void {
    this.plantId = Number(plantId);
    this.getData();
  }

  dateChange(date: DashboardDate): void {
    this.date = date;
    this.getData();
  }

  private getApiData(): void {
    const ordersStatus$ = this.plantId > 0
      ? this.OrdersService.getDashboardOrdersStatus(this.plantId, this.date.dateYMD)
      : of(this.createEmptyOrdersStatus());

    forkJoin({
      deviations: this.ProjectsService.getDeviations(),
      plantsData: this.ProjectsService.getDashboardOperational(this.date.dateYMD),
      ordersStatus: ordersStatus$,
      orderDeliveries: this.OrdersService.getDashboardOrderdeliveries(this.date.dateYMD),
      plantCapacities: this.OrdersService.getDashboardPlantCapacities(this.date.dateYMD)
    }).subscribe({
      next: responses => {
        const ordersStatus = this.unwrapData<OrdersStatus>(responses.ordersStatus);
        this.applyData({
          plants: this.plants,
          plantsData: this.unwrapData<PlantData[]>(responses.plantsData) ?? [],
          deviations: this.unwrapData<ProjectDeviations[]>(responses.deviations) ?? [],
          ordersStatusByPlant: this.plantId > 0 && ordersStatus
            ? { [this.plantId]: ordersStatus }
            : {},
          orderDeliveries: this.unwrapData<OrderDeliveries>(responses.orderDeliveries)
            ?? this.createEmptyOrderDeliveries(),
          plantCapacities: this.unwrapData<PlantCapacities>(responses.plantCapacities)
            ?? this.createEmptyPlantCapacities()
        });
      },
      error: error => console.error(error)
    });
  }

  private applyData(data: ProjectsOrdersDashboardData): void {
    console.log(data)

    if(data.plants.length)
      this.plants = data.plants;
    this.plantsData = data.plantsData;
    this.deviations = data.deviations;
    this.ordersStatus = this.plantId > 0
      ? data.ordersStatusByPlant[this.plantId] ?? this.createEmptyOrdersStatus()
      : this.createEmptyOrdersStatus();
    this.orderDeliveries = this.filterOrderDeliveries(data.orderDeliveries);
    this.plantCapacities = this.filterPlantCapacities(data.plantCapacities);

    this.setSummary();
    this.setdeviationsSummary();
  }

  private filterOrderDeliveries(data: OrderDeliveries): OrderDeliveries {
    if(this.plantId <= 0)
      return data;

    const items = data.items.filter(item => item.plant.id === this.plantId);
    const result = this.createEmptyOrderDeliveries();
    result.items = items;
    for(const item of items) {
      if(item.status === OrderStatus.running)
        result.summary.running++;
      else if(item.status === OrderStatus.upcoming)
        result.summary.upcoming++;
      else if(item.status === OrderStatus.onCall)
        result.summary.onCall++;
      else if(item.status === OrderStatus.laggingBehind)
        result.summary.overdue++;
    }
    return result;
  }

  private filterPlantCapacities(data: PlantCapacities): PlantCapacities {
    if(this.plantId <= 0)
      return data;

    return {
      onCall: { ...data.onCall },
      plants: data.plants.filter(item => item.plant.id === this.plantId)
    };
  }

  private createDeviationSummary(deviation: ProjectDeviations): DashboardDeviation {
    return {
      subjectType: deviation.subjectType,
      category: this.getDeviationCategory(deviation),
      type: deviation.type,
      severity: this.isBlockingDeviation(deviation)
        ? DeviationSeverity.blocking
        : DeviationSeverity.warning,
      subjectID: 0,
      description: this.getDeviationDescription(deviation),
      count: deviation.count
    };
  }

  private getDeviationCategory(deviation: ProjectDeviations): number {
    if(deviation.subjectType === DeviationSubjectType.project)
      return DeviationCategory.planning;

    if(deviation.subjectType === DeviationSubjectType.order && (
      deviation.type === OrderDeviationType.multi_day_price_changed ||
      deviation.type === OrderDeviationType.missing_price_usage
    ))
      return DeviationCategory.pricing;

    return DeviationCategory.production;
  }

  private isBlockingDeviation(deviation: ProjectDeviations): boolean {
    if(deviation.subjectType === DeviationSubjectType.project)
      return true;

    if(deviation.subjectType === DeviationSubjectType.order)
      return [
        OrderDeviationType.missing_recipe,
        OrderDeviationType.not_planned,
        OrderDeviationType.not_produced,
        OrderDeviationType.overdue,
        OrderDeviationType.missing_price_usage
      ].includes(deviation.type);

    if(deviation.subjectType === DeviationSubjectType.receipt)
      return [ReceiptDeviationType.cancelled, ReceiptDeviationType.missing_amount].includes(deviation.type);

    return false;
  }

  private getDeviationDescription(deviation: ProjectDeviations): string {
    const descriptions: Record<string, string> = {
      [`${DeviationSubjectType.project}:${ProjectDeviationType.missing_location}`]: 'Projecten zonder locatie',
      [`${DeviationSubjectType.project}:${ProjectDeviationType.missing_travel_info}`]: 'Projecten zonder reisinformatie',
      [`${DeviationSubjectType.order}:${OrderDeviationType.missing_recipe}`]: 'Orders zonder receptuur',
      [`${DeviationSubjectType.order}:${OrderDeviationType.not_planned}`]: 'Niet-geplande orders',
      [`${DeviationSubjectType.order}:${OrderDeviationType.multi_day_price_changed}`]: 'Orders met gewijzigde meerdaagse prijs',
      [`${DeviationSubjectType.order}:${OrderDeviationType.not_produced}`]: 'Niet-geproduceerde orders',
      [`${DeviationSubjectType.order}:${OrderDeviationType.partially_produced}`]: 'Gedeeltelijk geproduceerde orders',
      [`${DeviationSubjectType.order}:${OrderDeviationType.overproduced}`]: 'Overgeproduceerde orders',
      [`${DeviationSubjectType.order}:${OrderDeviationType.other_producing_plant}`]: 'Orders geproduceerd op een andere centrale',
      [`${DeviationSubjectType.order}:${OrderDeviationType.overdue}`]: 'Achterstallige orders',
      [`${DeviationSubjectType.order}:${OrderDeviationType.lagging_behind}`]: 'Orders met achterstand',
      [`${DeviationSubjectType.order}:${OrderDeviationType.missing_price_usage}`]: 'Orders zonder gebruiksprijs',
      [`${DeviationSubjectType.receipt}:${ReceiptDeviationType.cancelled}`]: 'Geannuleerde productiebonnen',
      [`${DeviationSubjectType.receipt}:${ReceiptDeviationType.dropped}`]: 'Vervallen productiebonnen',
      [`${DeviationSubjectType.receipt}:${ReceiptDeviationType.missing_amount}`]: 'Productiebonnen zonder hoeveelheid'
    };

    return descriptions[`${deviation.subjectType}:${deviation.type}`] ?? 'Afwijkingen';
  }

  private setToday(): void {
    const today = new Date();
    const dateDMY = this.DatetimeService.getDateToDMY(today);
    this.date = {
      period: DashboardDayPeriod.Today,
      dateDMY,
      dateYMD: this.DatetimeService.dateDMYToYMD(dateDMY),
      dateUnixTime: new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime()
    };
  }

  private unwrapData<T>(response: any): T {
    return response?.data ?? response;
  }

  private createEmptySummary(): Summary {
    return {
      plannedAmount: 0,
      deliveredAmount: 0,
      remainingAmount: 0,
      projectCount: 0,
      orderCount: 0,
      progressPercentage: 0,
      deviationCount: 0
    };
  }

  private createEmptyOrdersStatus(): OrdersStatus {
    return {
      orderCount: 0,
      completed: 0,
      completedPercentage: 0,
      partlyDelivered: 0,
      partlyDeliveredPercentage: 0,
      notStarted: 0,
      notStartedPercentage: 0,
      onCall: 0,
      receiptsCount: 0
    };
  }

  private createEmptyOrderDeliveries(): OrderDeliveries {
    return {
      summary: { running: 0, upcoming: 0, onCall: 0, overdue: 0 },
      items: []
    };
  }

  private createEmptyPlantCapacities(): PlantCapacities {
    return {
      onCall: { orderCount: 0, amount: 0 },
      plants: []
    };
  }
}
